export type ErrorContext = {
  product: 'wallet' | 'management' | 'integrations';
  errorReference: string;
  errorSignature: string;
  occurrenceCount: number;
  route: string;
  appVersion: string;
  environment: string;
  trackId?: string;
  userId?: string;
  orgId?: string;
  sessionId?: string;
};

const STORAGE_KEY = 'vm.s';
const SESSION_KEY = 'vm.sid';
const COOLDOWN_MS = 5 * 60 * 1000;

/**
 * Stable opaque id for the current tab session, so every error from one session
 * can be correlated in New Relic. Minted once and reused for the session; falls
 * back to a fresh (non-persisted) id when storage is unavailable, so correlation
 * is best-effort and the caller never throws.
 */
export function getOrCreateSessionId(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null,
  rand: () => number = Math.random,
): string {
  const mint = () =>
    Array.from({ length: 2 }, () =>
      Math.floor(rand() * 0x10000)
        .toString(16)
        .padStart(4, '0'),
    ).join('');
  if (!storage) return mint();
  try {
    const existing = storage.getItem(SESSION_KEY);
    if (existing) return existing;
    const sid = mint();
    storage.setItem(SESSION_KEY, sid);
    return sid;
  } catch {
    return mint();
  }
}

type OccurrenceRecord = { sig: string; count: number; ts: number };

/** Stable, opaque hash of message plus a slice of component stack or stack. */
export function computeErrorSignature(
  error: { message?: string; stack?: string },
  componentStack?: string,
): string {
  const basis = `${error.message ?? ''}::${(componentStack ?? error.stack ?? '').slice(0, 300)}`;
  let h = 5381;
  for (let i = 0; i < basis.length; i += 1) {
    h = (Math.imul(h, 33) ^ basis.charCodeAt(i)) >>> 0;
  }
  return h.toString(36);
}

/**
 * Count occurrences of the same signature in sessionStorage. The stored value is
 * an already-hashed signature plus a count and timestamp, so nothing legible or
 * personal lands in storage. Falls back to transient when storage is unavailable.
 */
export function recordOccurrence(
  signature: string,
  now: number,
  storage: Pick<Storage, 'getItem' | 'setItem'> | null,
): { count: number; isRecurring: boolean } {
  if (!storage) return { count: 1, isRecurring: false };
  let prev: OccurrenceRecord | null = null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    prev = raw ? (JSON.parse(raw) as OccurrenceRecord) : null;
  } catch {
    prev = null;
  }
  const fresh = !prev || prev.sig !== signature || now - prev.ts > COOLDOWN_MS;
  const count = fresh ? 1 : prev.count + 1;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ sig: signature, count, ts: now }));
  } catch {
    // storage write blocked, ignore
  }
  return { count, isRecurring: count >= 2 };
}

/** Per-occurrence user-facing reference. */
export function generateErrorReference(rand: () => number = Math.random): string {
  const block = () =>
    Math.floor(rand() * 0x10000)
      .toString(16)
      .toUpperCase()
      .padStart(4, '0')
      .slice(0, 4);
  return `VM-${block()}-${block()}`;
}

/** Keep the path, drop query and hash (which may carry PII such as email). */
export function routeWithoutQuery(pathnameOrUrl: string): string {
  let end = pathnameOrUrl.length;
  const q = pathnameOrUrl.indexOf('?');
  const hash = pathnameOrUrl.indexOf('#');
  if (q >= 0) end = Math.min(end, q);
  if (hash >= 0) end = Math.min(end, hash);
  return pathnameOrUrl.slice(0, end);
}

/** Build the New Relic attribute bag. Locators only, never raw personal data. */
export function buildNewRelicAttributes(
  componentStack: string,
  ctx: ErrorContext,
): Record<string, string | number | boolean> {
  const attrs: Record<string, string | number | boolean> = {
    errorReference: ctx.errorReference,
    errorSignature: ctx.errorSignature,
    occurrenceCount: ctx.occurrenceCount,
    product: ctx.product,
    // Strip here too, so the locators-not-data guarantee holds even if a caller
    // forgets to pre-strip (query/hash can carry email, tokens, trackId).
    route: routeWithoutQuery(ctx.route),
    appVersion: ctx.appVersion,
    environment: ctx.environment,
    componentStack: componentStack.slice(0, 1000),
  };
  if (ctx.trackId) attrs.trackId = ctx.trackId;
  if (ctx.userId) attrs.userId = ctx.userId;
  if (ctx.orgId) attrs.orgId = ctx.orgId;
  if (ctx.sessionId) attrs.sessionId = ctx.sessionId;
  return attrs;
}
